import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { MFTextField } from "../../../../lib/formik";
import { TextDatePicker } from "../../../onboarding/DatePickerWithTextFeild";
import MFSelectField from "../../../../lib/formik/SelectField";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    FormikContextType,
    useFormikContext
} from "formik";
import {
    allowDecimalNumbers,
    formatCurrency,
} from "../../../../utils/utilityFunctions";
import dayjs from "dayjs";
import { CurrencyConversionToWords } from "../commonComponents";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import * as yup from 'yup';

export default function AifForm({
    securitiesData,
    fetchSecuritiesData,
    setValidations,
    isLoading
}: {
    securitiesData: Record<string, any>;
    fetchSecuritiesData: (searchInput: string) => void;
    setValidations: (values: Record<string, any>) => void;
    isLoading: boolean;
}): JSX.Element {
    const { currencySymbol, locale = "en-IN", currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        values,
        setFieldValue,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleDeferralExceptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue("deferralException", event.target.value)
    };

    const handleOrderDateChange = (selectedDate: any) => {
        setFieldValue('orderDate', selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleCurrencyFieldPaste = (event: React.ClipboardEvent<HTMLInputElement>, fieldName: string) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")

        const modifiedText = (`${clipboardData ?? ""}`)?.replaceAll(",", "")

        setFieldValue(fieldName, modifiedText);
    }

    const handleTransactionTypeChange = async (event: SelectChangeEvent<unknown>) => {
        if (event.target.value === "Initial Contribution") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup.string(),
                commitmentAmount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Commitment Amount is required')
                    .test('is-number', 'Commitment Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Commitment Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    }),
                // .test('whole-number', 'Commitment Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // }),
                amount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                    // .test('whole-number', 'Amount should be a valid number', function (value) {
                    //     return numberRegex.test(`${value ?? ""}`);
                    // })
                    .test("less-than-equal-to-commitment-amount", 'Amount should not be more than commitment amount', function (value, context) {
                        const { parent } = context;
                        return Number(value) <= Number(`${parent?.commitmentAmount ?? ""}`?.replaceAll(',', ''))
                    })
            }))
        }

        if (event.target.value === "Drawdown Contribution") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup.string(),
                commitmentAmount: yup.string(),
                amount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                    // .test('whole-number', 'Amount should be a valid number', function (value) {
                    //     return numberRegex.test(`${value ?? ""}`);
                    // })
                    .test("less-than-equal-to-commitment-amount", 'Amount should not be more than commitment amount', function (value, context) {
                        const { parent } = context;
                        return Number(value) <= Number(`${parent?.commitmentAmount ?? ""}`?.replaceAll(',', ''))
                    })
            }))
        }

        if (event.target.value === "Redemption") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup
                    .string()
                    .required('Quantity is required')
                    .test('is-number', 'Quantity should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Quantity should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                    .test("less-than-equal-to-outstanding-units", 'Quantity should be less than or equal to Outstanding Units', function (value, context) {
                        const { parent } = context;

                        return Number(value) <= Number(parent?.security?.qtySaleable ?? "")
                    }),
                commitmentAmount: yup.string(),
                amount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                // .test('whole-number', 'Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // })
                // .test("less-than-equal-to-commitment-amount", 'Amount should be less than or equal to Current Market Value', function (value, context) {
                //     const { parent } = context;

                //     const calculatedMarketValue = Number(parent?.security?.qtySaleable ?? "") * Number(parent?.security?.marketPrice ?? "")

                //     return Number(value) <= Number(calculatedMarketValue)
                // })
            }))
        }

        if (event.target.value === "Top Up") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup.string(),
                commitmentAmount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Commitment Amount is required')
                    .test('is-number', 'Commitment Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Commitment Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    }),
                // .test('whole-number', 'Commitment Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // }),
                amount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                // .test('whole-number', 'Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // })
            }))
        }

        resetForm({
            values: {
                ...values,
                transactionType: event.target.value,
                security: null,
                issuer: "",
                quantity: "",
                lastAvailableNav: "",
                currentMarketValue: "",
                commitmentAmount: "",
                amount: "",
                settlementBankNumber: null,
                settlementBank: "",
                settlementMethod: ""
            }
        })
    }

    const handleSecurityChange = (selectedObj: Record<string, any>) => {
        // Formula to calculate Current market value => Outstanding Units * last Available NAV 
        const calculatedMarketValue = Number(selectedObj?.qtySaleable ?? "") * Number(selectedObj?.marketPrice ?? "")

        resetForm({
            values: {
                ...values,
                security: selectedObj,
                issuer: selectedObj?.issuerName ?? "",
                quantity: selectedObj ?
                    ["Initial Contribution", "Drawdown Contribution", "Top Up"].includes(values.transactionType) ? 0 : ""
                    : "",
                lastAvailableNav: selectedObj ?
                    Number(selectedObj?.marketPrice ?? "")?.toLocaleString(locale)
                    : "",
                currentMarketValue: selectedObj ?
                    calculatedMarketValue?.toLocaleString(locale)
                    : "",
                ...(["Initial Contribution", "Top Up"].includes(values?.transactionType) ? {
                    commitmentAmount: "",
                    amount: ""
                } : {}),
                ...(["Redemption", "Drawdown Contribution"].includes(values?.transactionType) ? {
                    commitmentAmount: selectedObj ?
                        (!Number.isNaN(Number(selectedObj?.commitmentAmt ?? 0)) ? Number(selectedObj?.commitmentAmt ?? 0) : 0)?.toLocaleString(locale, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4
                        }) ?? ""
                        : "",
                    amount: ""
                } : {}),
                settlementBankNumber: selectedObj
                    ? {
                        bankCode: selectedObj?.bankCode ?? "",
                        bankAccCcy: selectedObj?.bankAccCcy ?? "",
                        bankAccType: selectedObj?.bankAccType ?? "",
                        bankAccNo: selectedObj?.bankAccNo ?? "",
                        bankAccCode: selectedObj?.bankAccCode ?? "",
                        bankName: selectedObj?.bankName ?? "",
                        label: selectedObj?.bankAccNo ?? "",
                        value: selectedObj?.bankAccNo ?? ""
                    }
                    : null,
                settlementBank: selectedObj?.bankName ?? "",
            }
        })
    }

    const handleQuantityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        // Formula to calculate amount in case of Transaction Type: Redemption =>
        // Amount = quantity * last outstanding nav

        const calculatedAmount = Number(event.target.value) * Number(`${values?.lastAvailableNav ?? ""}`?.replaceAll(',', ''))

        resetForm({
            values: {
                ...values,
                quantity: event.target.value,
                ...(values?.transactionType === "Redemption" ? {
                    amount: Number(calculatedAmount)?.toLocaleString(locale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4
                    })
                } : {})
            }
        })
    }

    const refactoredCurrentMarketValue = `${values?.currentMarketValue ?? ""}`?.replaceAll(',', '')
    const refactoredLastAvailableNav = `${values?.lastAvailableNav ?? ""}`?.replaceAll(',', '')
    const refactoredCommitmentAmount = `${values?.commitmentAmount ?? ""}`?.replaceAll(',', '')
    const refactoredAmount = `${values?.amount ?? ""}`?.replaceAll(',', '')
    const refactoredPastCommitmentAmount = formatCurrency({
        value: !Number.isNaN(Number(values?.security?.commitmentAmt ?? 0)) ? Number(values?.security?.commitmentAmt ?? 0) : 0,
        locale,
        currencyCode
    })


    return (
        <Grid
            container
            columnSpacing={3}
        >
            {/* <Grid item xs={12} lg={4}>
                <MFTextField
                    name="orderNumber"
                    label="Order Number"
                    placeholder=""
                />
            </Grid> */}
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="orderDate"
                    label="Order Date *"
                    placeholder=""
                    maxDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={handleOrderDateChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="transactionType"
                    label="Transaction Type *"
                    items={[
                        { name: "Redemption" }, //call holding api
                        { name: "Initial Contribution" },
                        { name: "Drawdown Contribution" }, //call holdings api
                        { name: "Top Up" } //call holding api
                    ].map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleTransactionTypeChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="security"
                    label="Security *"
                    options={securitiesData?.securities?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: obj?.securityName ?? "",
                        value: obj?.security ?? ""
                    })) ?? []}
                    fetchData={fetchSecuritiesData}
                    disabled={isLoading || !values?.transactionType}
                    onChange={handleSecurityChange}
                    filterOptions={(x) => x}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="issuer"
                    label="Issuer"
                    placeholder=""
                    disabled={true}
                    value={values?.issuer ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4} sx={{ position: "relative" }}>
                {values?.security && (
                    <Typography
                        variant="overline_400"
                        sx={{
                            position: "absolute",
                            top: "-5px",
                            right: "0px"
                        }}
                    >
                        Outstanding Units: {values?.security?.qtySaleable ?? ""}
                    </Typography>
                )}
                <MFTextField
                    name="quantity"
                    label={`Quantity ${["Redemption"].includes(values.transactionType) ? "*" : ""}`}
                    placeholder=""
                    disabled={
                        isLoading ||
                        !values?.security ||
                        (["Initial Contribution", "Drawdown Contribution", "Top Up"].includes(values.transactionType))
                    }
                    value={values?.quantity ?? ""}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4,
                        useGrouping: false
                    }}
                    onChange={handleQuantityChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="lastAvailableNav"
                    label="Last Available NAV"
                    placeholder=""
                    startAdornment={values?.lastAvailableNav ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    formatCurrency
                    disabled={true}
                    value={values?.lastAvailableNav ?? ""}
                    onKeyDown={allowDecimalNumbers}
                />
                <CurrencyConversionToWords value={refactoredLastAvailableNav} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="currentMarketValue"
                    label="Current Market Value"
                    placeholder=""
                    startAdornment={values?.currentMarketValue ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    formatCurrency
                    disabled={true}
                    value={values?.currentMarketValue ?? ""}
                    onKeyDown={allowDecimalNumbers}
                />
                <CurrencyConversionToWords value={refactoredCurrentMarketValue} />
            </Grid>
            <Grid item xs={12} lg={4} sx={{ position: "relative" }}>
                {values?.transactionType && (
                    <Typography
                        variant="overline_400"
                        sx={{
                            position: "absolute",
                            top: "-5px",
                            right: "0px"
                        }}
                    >
                        Past Commitment Amount: {refactoredPastCommitmentAmount}
                    </Typography>
                )}
                <MFTextField
                    name="commitmentAmount"
                    label={`Commitment Amount ${["Initial Contribution", "Top Up"].includes(values.transactionType) ? "*" : ""}`}
                    placeholder=""
                    startAdornment={values?.commitmentAmount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (
                                    isLoading ||
                                    !values?.security ||
                                    ["Drawdown Contribution", "Redemption"].includes(values?.transactionType)
                                ) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={
                        isLoading ||
                        !values?.security ||
                        ["Drawdown Contribution", "Redemption"].includes(values?.transactionType)
                    }
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4,
                    }}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "commitmentAmount")}
                    value={values?.commitmentAmount ?? ""}
                />
                <CurrencyConversionToWords value={refactoredCommitmentAmount} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="amount"
                    label={`Amount *`}
                    placeholder=""
                    startAdornment={values?.amount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.security) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={isLoading || !values?.security}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4,
                    }}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "amount")}
                    value={values?.amount ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAmount} />
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "20px" }}>
                <Typography
                    variant="body_500"
                    sx={(theme: Theme) => ({
                        lineHeight: "20px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.secondary
                    })}
                >
                    Deferral/Exception
                </Typography>
                <FormControl sx={{ marginTop: "15px" }}>
                    <RadioGroup
                        row
                        value={values?.deferralException}
                        onChange={handleDeferralExceptionChange}
                        defaultValue="No"
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            rowGap: "10px"
                        }}
                    >
                        {[
                            { deferralExceptionLabel: "Yes", deferralExceptionValue: "Yes" },
                            { deferralExceptionLabel: "No", deferralExceptionValue: "No" }
                        ].map(({ deferralExceptionLabel, deferralExceptionValue }) => (
                            <FormControlLabel
                                key={deferralExceptionValue}
                                value={deferralExceptionValue}
                                control={
                                    <Radio
                                        size="medium"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.primary[500],
                                            "&.MuiRadio-root": {
                                                padding: "0px",
                                            },
                                            ...(values?.deferralException !== deferralExceptionValue ? {
                                                stroke: theme.palette.primary[50],
                                            } : {}),
                                        })}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            component="span"
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                letterSpacing: '0.02px',
                                                color: values?.deferralException === deferralExceptionValue ?
                                                    theme.palette.primary[500] :
                                                    theme.palette.common.black,
                                            })}
                                        >
                                            {deferralExceptionLabel}
                                        </Typography>
                                    </Box>
                                }
                                sx={(theme: Theme) => ({
                                    borderRadius: '10px',
                                    backgroundColor: values?.deferralException === deferralExceptionValue ?
                                        theme.palette.primary[50] :
                                        `${theme.palette.common.white}0D`,
                                    padding: values?.deferralException === deferralExceptionValue ? '7px 12px' : '8px 12px',
                                    border: values?.deferralException === deferralExceptionValue ?
                                        `1px solid ${theme.palette.primary[500]}` :
                                        `0.5px solid ${theme.palette.text.secondary}80`,
                                    "&.MuiFormControlLabel-root": {
                                        columnGap: "10px",
                                        marginLeft: "0px",
                                        marginRight: "12px"
                                    },
                                })}
                                disabled={isLoading}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "20px" }}>
                <MFTextField
                    name="remarks"
                    label="Remarks *"
                    placeholder=""
                    multiline
                    inputProps={{ maxLength: 200 }}
                    value={values?.remarks ?? ""}
                    disabled={isLoading}
                />
                <Typography
                    variant="overline_400"
                    sx={(theme: Theme) => ({
                        textAlign: "end",
                        color: theme.palette.text.secondary,
                        marginTop: "-10px"
                    })}
                >
                    Max 200 characters
                </Typography>
            </Grid>
        </Grid>
    )
}